import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as d3 from 'd3';
import { gexService } from '../services/gexService';
import { fetchExpirations, daysToExpiry } from '../services/gammaService';
import { profileService } from '../services/profileService';
import { TickerLinkButton } from './TickerLinkButton';
import { drawCandles } from './gex-profile/drawCandles';
import { COLORS, MARGINS, GEX_MARGINS, GEX_MARGINS_NARROW, METRIC_LABELS, WATERMARK_PATH, WATERMARK_OPACITY, watermarkSize } from './gex-profile/constants';
import type { GexStrikeData, GexMetric, GexDisplayMode, GexWidgetSettings } from '../types/gex';
import { rankWalls } from './gexWalls';
import type { TooltipData } from './gex-profile/types';
import { profileRange } from './gexProfileRange';
import { useTickerLink } from '../contexts/TickerLinkContext';

interface GexProfileWidgetProps {
  windowId: string;
  onRemove: () => void;
  /**
   * Pins the widget to one symbol and hides the symbol controls.
   *
   * Used where the page already owns the ticker — Ticker Explorer — so the
   * widget cannot drift away from the symbol around it. Left undefined on
   * dashboards, where picking the symbol is the whole point.
   */
  lockedSymbol?: string;
  /**
   * Seeds the widget's settings on first use. Anything omitted falls back to
   * DEFAULT_SETTINGS, and anything the user then changes is saved per
   * windowId and wins on the next visit — these are starting points, not a
   * lock.
   */
  defaultSettings?: Partial<GexWidgetSettings>;
  /**
   * Re-selects the soonest expiry every time the list changes, instead of
   * keeping whatever was saved.
   *
   * ⚠️ **Only correct where the user cannot pick an expiry.** The normal
   * behaviour keeps a saved date as long as it is still a real expiry, which
   * is right on a dashboard — a deliberate choice should survive a reload.
   * But a panel titled "Next Expiry" with no controls has to *mean* it: a
   * date saved yesterday is still valid today, so the panel would sit on
   * tomorrow's chain and look entirely plausible doing it. That is what SPY
   * showed — 08/05 on the 4th, with a 0DTE chain listed and ignored.
   */
  pinNearestExpiry?: boolean;
  /**
   * Hides the whole toolbar — symbol, metric, display mode, expiry, refresh,
   * chart toggle, settings and close.
   *
   * For places that configure the widget themselves and present it as a fixed
   * readout rather than something to fiddle with. Settings are still honoured,
   * just not editable from here.
   */
  hideControls?: boolean;
  /**
   * Replaces the 'Gamma Exposure — SYMBOL' header.
   *
   * Where the page already states the ticker, repeating it is noise; a title
   * naming what the panel measures is worth more.
   */
  title?: string;
  /**
   * Turns off wheel-zoom, drag-pan and double-click-reset.
   *
   * For pages that present the chart as a fixed readout. The scroll wheel
   * belongs to the page there — a chart that swallows it makes the section
   * feel like a trap.
   */
  disableZoom?: boolean;
}

const DEFAULT_SETTINGS: GexWidgetSettings = {
  symbol: 'SPY',
  daysBack: 60,
  metric: 'gex',
  displayMode: 'separate',
  byExpiration: false,
  expiryDate: null,
  showCurrentPriceLine: true,
  showCandlesticks: true,
};

/** Sentinel for the "aggregate every expiry" option. Not a date, so it can
    never collide with one. */
const ALL_EXPIRIES = '__all__';

/**
 * How far either side of spot the profile-only view spans.
 *
 * Only applies when candles are hidden — with candles on, the range comes from
 * the price history instead.
 */

// The brand faces, for SVG text where a Tailwind class cannot reach.
// Archivo for chrome, Inter for numbers — the same split as the rest of the app.
const LABEL_FONT = 'Archivo, ui-sans-serif, system-ui, sans-serif';
const NUMERIC_FONT = 'Inter, ui-sans-serif, system-ui, sans-serif';

/** MM/DD/YYYY, matching the rest of the app. Parsed as UTC so the calendar
    date is never shifted by the viewer's timezone. */
function fmtExpiryLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      });
}

function formatGexValue(val: number | unknown): string {
  const v = Number(val) || 0;
  const absVal = Math.abs(v);
  if (absVal >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (absVal >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (absVal >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return v.toFixed(0);
}

/**
 * View toggle, matching GEX-Ray's button of the same name.
 *
 * Active is a SOLID Accent Gold fill with dark text, not a tinted background.
 * This took a per-button `color` and rendered a 20%-alpha wash of it — which
 * meant the one caller passed Up/Call blue, a colour that means "call side" on
 * the chart inches away. Reusing it for "this toggle is on" invited reading a
 * view control as a call/put one. Gold carries no directional meaning, which is
 * what a UI-state colour needs.
 *
 * Classes rather than inline styles, so the palette sweep reaches it — inline
 * hexes are exactly how the off-brand ones survived last time.
 */
function ViewButton({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
        isActive
          ? 'bg-accent-yellow border-accent-yellow text-hot-tag-text font-semibold'
          : 'bg-transparent border-dark-border text-gray-500 hover:text-gray-300 hover:border-gray-600'
      }`}
    >
      {label}
    </button>
  );
}

export function GexProfileWidget({ windowId, onRemove, lockedSymbol, defaultSettings, hideControls, title, disableZoom, pinNearestExpiry = false }: GexProfileWidgetProps) {
  // Whether this pane is following the shared ticker. The box goes
  // read-only while it is — see the note on the input.
  const { isWindowLinked } = useTickerLink();
  const isTickerLinked = isWindowLinked(windowId);
  // -- DOM refs --
  const candleCanvasRef = useRef<HTMLDivElement>(null);
  const gexSvgContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // -- D3 refs (not React state -- D3 manages its own render loop) --
  const yScaleBaseRef = useRef<d3.ScaleLinear<number, number> | null>(null);
  const xScaleRef = useRef<d3.ScaleTime<number, number> | null>(null);
  const zoomRef = useRef<d3.ZoomBehavior<Element, unknown> | null>(null);
  const currentTransformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  const watermarkImageRef = useRef<HTMLImageElement | null>(null);

  // -- React state --
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadedRef = useRef(false);

  const [settings, setSettings] = useState<GexWidgetSettings>(() => ({ ...DEFAULT_SETTINGS, ...defaultSettings }));
  const [symbolInput, setSymbolInput] = useState(DEFAULT_SETTINGS.symbol);

  // A locked symbol wins over the saved one, and is deliberately NOT written
  // back into settings: the widget keeps whatever symbol it had on a
  // dashboard, so unlocking it later restores that rather than stranding it
  // on whichever ticker the page happened to show.
  const activeSymbol = lockedSymbol ?? settings.symbol;
  const [showSettings, setShowSettings] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [candleTooltip, setCandleTooltip] = useState<{
    x: number; y: number; date: string; open: number; high: number; low: number; close: number;
  } | null>(null);

  // Pane divider drag state
  const [gexPanelWidth, setGexPanelWidth] = useState(settings.gexPanelWidth ?? 200);
  const dividerDragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Keep refs to latest data so renderAll() can access without stale closures
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const tooltipRef = useRef(tooltip);
  tooltipRef.current = tooltip;
  const ohlcDataRef = useRef<ReturnType<typeof useQuery<Awaited<ReturnType<typeof gexService.fetchOHLC>>>>['data']>(undefined);
  const gexDataRef = useRef<GexStrikeData[] | undefined>(undefined);

  // -- Load watermark image --
  useEffect(() => {
    const img = new Image();
    img.src = WATERMARK_PATH;
    img.onload = () => {
      watermarkImageRef.current = img;
    };
  }, []);

  // -- Load state from backend on mount --
  useEffect(() => {
    const loadState = async () => {
      try {
        const response = await profileService.getFrontendState();
        const savedState = response.frontend_state?.gexProfileWidgets?.[windowId] as GexWidgetSettings | undefined;
        if (savedState) {
          setSettings({ ...DEFAULT_SETTINGS, ...defaultSettings, ...savedState });
          setSymbolInput(savedState.symbol);
          if (savedState.gexPanelWidth) {
            setGexPanelWidth(savedState.gexPanelWidth);
          }
        }
        isLoadedRef.current = true;
      } catch (error) {
        console.error('Failed to load Gamma Exposure state:', error);
        isLoadedRef.current = true;
      }
    };
    loadState();
  }, [windowId]);

  // -- Save state to backend (debounced) --
  useEffect(() => {
    if (!isLoadedRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await profileService.saveFrontendState({
          gexProfileWidgets: { [windowId]: settings },
        });
      } catch (error) {
        console.error('Failed to save Gamma Exposure state:', error);
      }
    }, 1000);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [windowId, settings]);

  // -- Data fetching --
  const { data: ohlcData, isLoading: ohlcLoading, error: ohlcError, refetch: refetchOhlc } = useQuery({
    queryKey: ['gexOhlc', activeSymbol, settings.daysBack],
    queryFn: () => gexService.fetchOHLC(activeSymbol, settings.daysBack),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  /**
   * True when this pane is pinned to a single expiry that has not resolved yet.
   *
   * ⚠️ **The reason this exists rather than a fallback.** The query below used
   * to serve the all-expiries aggregate whenever `expiryDate` was null, on the
   * reasoning that some data beats none. For a toolbar pane that is fine — the
   * dropdown reads "All Expiries" and says so. For a pane configured with
   * `byExpiration` and its controls hidden, it is a lie: the panel is titled
   * for a single expiry, the expiry chip in the title bar is itself gated on
   * `expiryDate` so it renders nothing, and the reader is shown every expiry
   * summed under a "Next Expiry" heading with no indication anything is wrong.
   *
   * Two panes side by side, one all-expiries and one pinned, rendered byte
   * identical charts and neither reported an error.
   */
  const awaitingExpiry = settings.byExpiration && !settings.expiryDate;

  const { data: gexData, isLoading: gexLoading, error: gexError, refetch: refetchGex } = useQuery({
    queryKey: ['gexByStrike', activeSymbol, settings.byExpiration, settings.expiryDate],
    queryFn: () =>
      settings.expiryDate
        ? gexService.fetchGexByStrikeExpiry(activeSymbol, undefined, settings.expiryDate)
        : gexService.fetchGexByStrike(activeSymbol),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    // Ask for nothing rather than for the wrong thing. The expiry list resolves
    // this within a tick when it is available at all; when it is not, the pane
    // renders the unavailable state below instead of a mislabelled chart.
    enabled: !awaitingExpiry,
  });

  // Keep data refs current
  ohlcDataRef.current = ohlcData;
  gexDataRef.current = gexData;

  // -- Handlers --
  // Real contract expiries for this symbol. Only fetched when the By-Expiry
  // mode is actually on — most sessions never open it.
  const { data: expirationsData } = useQuery({
    queryKey: ['qfExpirations', activeSymbol],
    queryFn: () => fetchExpirations(activeSymbol),
    staleTime: 60 * 60 * 1000,
    // NOT gated on byExpiration. The dropdown *is* the All-vs-single control,
    // so it has to be populated before a single expiry is ever chosen —
    // gating it here meant a widget defaulting to All never fetched the list
    // and silently fell back to the old toggle plus date box.
    enabled: !!activeSymbol,
    // One attempt. Until QF deploys `/v1/expirations` this 404s every time,
    // and retrying only delays the unavailable state.
    retry: false,
  });
  // ⚠️ `expirations`, not `rows`. The sibling /v1/gex-by-expiry route keys its
  // list `expiries`. Both verified against QF's response models — reading the
  // wrong key yields [] and renders as "no data" on a perfectly good 200.
  const expirations = expirationsData?.expirations ?? [];

  // Snap the selection onto a real expiry.
  //
  // Runs when the list arrives, when the symbol changes, and when By-Expiry is
  // switched on. Covers three cases that all used to render an empty panel:
  // nothing selected yet, a date saved from a previous session that has since
  // passed, and a date carried over from a symbol that expires on different
  // days from this one.
  useEffect(() => {
    if (!settings.byExpiration || !expirations.length) return;

    // Soonest first, and today counts — a 0DTE chain is a real expiry and is
    // exactly what someone opening this mid-session wants.
    const nearest = expirations[0].expiry;

    if (pinNearestExpiry) {
      if (settings.expiryDate !== nearest) {
        setSettings((prev) => ({ ...prev, expiryDate: nearest }));
      }
      return;
    }

    const isValid = expirations.some((e) => e.expiry === settings.expiryDate);
    if (isValid) return;
    setSettings((prev) => ({ ...prev, expiryDate: nearest }));
  }, [expirations, settings.byExpiration, settings.expiryDate, pinNearestExpiry]);

  const handleSymbolSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (symbolInput.trim()) {
      setSettings((prev) => ({ ...prev, symbol: symbolInput.trim().toUpperCase() }));
    }
  };

  const handleSymbolSelect = (symbol: string) => {
    const upperSymbol = symbol.toUpperCase();
    setSymbolInput(upperSymbol);
    setSettings((prev) => ({ ...prev, symbol: upperSymbol }));
  };

  const handleRefresh = () => {
    refetchOhlc();
    refetchGex();
  };

  // ================================================================
  // Core render function -- called by D3 zoom and data changes
  // ================================================================
  const renderAll = useCallback(() => {
    const s = settingsRef.current;
    const ohlc = ohlcDataRef.current;
    const gex = gexDataRef.current;
    const yBase = yScaleBaseRef.current;
    if (!yBase) return;

    // Apply current zoom transform to Y scale
    const yScale = currentTransformRef.current.rescaleY(yBase);
    const currentPrice = ohlc && ohlc.length > 0 ? ohlc[ohlc.length - 1].close : null;

    // -- Render candlestick canvas --
    if (s.showCandlesticks && canvasRef.current && ohlc && ohlc.length > 0 && xScaleRef.current) {
      const canvas = canvasRef.current;
      const container = candleCanvasRef.current;
      if (container) {
        const dpr = window.devicePixelRatio || 1;
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (w > 0 && h > 0) {
          canvas.width = w * dpr;
          canvas.height = h * dpr;
          canvas.style.width = `${w}px`;
          canvas.style.height = `${h}px`;

          // Update Y scale range for canvas height (high prices = small Y = top)
          const canvasYScale = yScale.copy().range([h - MARGINS.bottom, MARGINS.top]);

          drawCandles({
            canvas,
            data: ohlc,
            scales: { xScale: xScaleRef.current, yScale: canvasYScale },
            margins: MARGINS,
            currentPrice,
            showCurrentPriceLine: s.showCurrentPriceLine,
            watermarkImage: watermarkImageRef.current,
            crosshairPrice: tooltipRef.current?.strike ?? null,
          });
        }
      }
    }

    // -- Render GEX profile SVG --
    if (svgRef.current && gex && gex.length > 0) {
      const svg = d3.select(svgRef.current);
      const container = gexSvgContainerRef.current;
      if (!container) return;

      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) return;

      svg.attr('width', w).attr('height', h);

      const margins = w > 250 ? GEX_MARGINS : GEX_MARGINS_NARROW;
      const chartWidth = w - margins.left - margins.right;
      const chartHeight = h - margins.top - margins.bottom;

      // GEX Y scale matched to this panel's pixel range (high prices = small Y = top)
      const gexYScale = yScale.copy().range([h - margins.bottom, margins.top]);

      // Get field names for the selected metric
      const callField = `call_${s.metric}` as keyof GexStrikeData;
      const putField = `put_${s.metric}` as keyof GexStrikeData;

      // Filter data within visible price range
      const [pMin, pMax] = [gexYScale.invert(h - margins.bottom), gexYScale.invert(margins.top)];
      const validData = gex.filter((d) => d.strike >= pMin && d.strike <= pMax);

      // Calculate max value for bar scaling
      let maxValue = 0;
      if (s.displayMode === 'separate') {
        for (const d of validData) {
          maxValue = Math.max(maxValue, Math.abs(Number(d[callField]) || 0), Math.abs(Number(d[putField]) || 0));
        }
      } else {
        for (const d of validData) {
          maxValue = Math.max(maxValue, Math.abs((Number(d[callField]) || 0) + (Number(d[putField]) || 0)));
        }
      }

      // Bar height based on strike spacing
      const strikes = validData.map((d) => d.strike).sort((a, b) => a - b);
      let barHeight = chartHeight / 50;
      if (strikes.length > 1) {
        const avgGap = (strikes[strikes.length - 1] - strikes[0]) / (strikes.length - 1);
        const pxPerDollar = chartHeight / (pMax - pMin);
        barHeight = Math.max(2, Math.min(avgGap * pxPerDollar * 0.8, chartHeight / 20));
      }

      const centerX = margins.left + chartWidth * 0.5;

      // Where a level's name is drawn. With a wide gutter it goes beside the
      // strike, outside the plot entirely, and the bars keep the full width.
      // A narrow panel has no gutter to spare, so the label stays inside and
      // the bars give up a lane to make room for it.
      // Bars stop short of the right edge to leave a lane for the wall tags.
      // The tags say the tier only ('MAJOR' / 'MINOR'), with colour carrying
      // call versus put, so the lane is a good deal narrower than the fully
      // spelled-out labels needed.
      // Both sides shrink together: the profile is read by comparing
      // magnitudes, so an asymmetric scale would misrepresent it.
      const PILL_LANE = Math.min(68, chartWidth * 0.18);
      const halfSpan = Math.max(chartWidth * 0.5 - PILL_LANE, chartWidth * 0.28);
      const barScale = maxValue > 0 ? halfSpan / maxValue : 0;

      // -- Call / Put walls --------------------------------------------------
      // Ranked over the WHOLE chain (`gex`), not `validData` — see rankWalls.
      // The window only decides which of them are drawn.
      const { callWall, callWallMinor, putWall, putWallMinor } = rankWalls(gex, s.metric);

      // -- Clear and redraw SVG --
      svg.selectAll('*').remove();

      // Clip path
      svg.append('defs')
        .append('clipPath')
        .attr('id', `gex-clip-${windowId}`)
        .append('rect')
        .attr('x', margins.left)
        .attr('y', margins.top)
        .attr('width', chartWidth)
        .attr('height', chartHeight);

      const chartGroup = svg.append('g')
        .attr('clip-path', `url(#gex-clip-${windowId})`);

      // Watermark (when candles hidden)
      if (!s.showCandlesticks && watermarkImageRef.current) {
        const wmSize = watermarkSize(chartWidth, chartHeight);
        chartGroup.append('image')
          .attr('href', WATERMARK_PATH)
          .attr('x', margins.left + (chartWidth - wmSize) / 2)
          .attr('y', margins.top + (chartHeight - wmSize) / 2)
          .attr('width', wmSize)
          .attr('height', wmSize)
          .attr('opacity', WATERMARK_OPACITY);
      }

      // Grid lines
      const gridTicks = gexYScale.ticks(chartHeight > 300 ? 8 : 6);
      chartGroup.selectAll('.grid-line')
        .data(gridTicks)
        .join('line')
        .attr('x1', margins.left)
        .attr('x2', margins.left + chartWidth)
        .attr('y1', (d) => gexYScale(d))
        .attr('y2', (d) => gexYScale(d))
        .attr('stroke', COLORS.gridLine)
        .attr('stroke-width', 0.5);

      // Center zero line
      chartGroup.append('line')
        .attr('x1', centerX).attr('x2', centerX)
        .attr('y1', margins.top).attr('y2', margins.top + chartHeight)
        .attr('stroke', COLORS.axisLine)
        .attr('stroke-width', 0.5);

      // Bars
      if (maxValue > 0) {
        if (s.displayMode === 'separate') {
          // Call bars (right from center, blue)
          chartGroup.selectAll('.call-bar')
            .data(validData)
            .join('rect')
            .attr('class', 'call-bar')
            .attr('x', centerX)
            .attr('y', (d) => gexYScale(d.strike) - barHeight / 2)
            .attr('width', (d) => Math.abs(Number(d[callField]) || 0) * barScale)
            .attr('height', barHeight)
            .attr('rx', 1)
            .attr('fill', COLORS.callGex)
            .attr('opacity', 1);

          // Put bars (left from center, red)
          chartGroup.selectAll('.put-bar')
            .data(validData)
            .join('rect')
            .attr('class', 'put-bar')
            .attr('x', (d) => centerX - Math.abs(Number(d[putField]) || 0) * barScale)
            .attr('y', (d) => gexYScale(d.strike) - barHeight / 2)
            .attr('width', (d) => Math.abs(Number(d[putField]) || 0) * barScale)
            .attr('height', barHeight)
            .attr('rx', 1)
            .attr('fill', COLORS.putGex)
            .attr('opacity', 1);
        } else {
          // Net bars
          chartGroup.selectAll('.net-bar')
            .data(validData)
            .join('rect')
            .attr('class', 'net-bar')
            .attr('x', (d) => {
              const net = (Number(d[callField]) || 0) + (Number(d[putField]) || 0);
              return net >= 0 ? centerX : centerX - Math.abs(net) * barScale;
            })
            .attr('y', (d) => gexYScale(d.strike) - barHeight / 2)
            .attr('width', (d) => {
              const net = (Number(d[callField]) || 0) + (Number(d[putField]) || 0);
              return Math.abs(net) * barScale;
            })
            .attr('height', barHeight)
            .attr('rx', 1)
            .attr('fill', (d) => {
              const net = (Number(d[callField]) || 0) + (Number(d[putField]) || 0);
              return net >= 0 ? COLORS.callGex : COLORS.putGex;
            })
            .attr('opacity', 1);
        }
      }

      // -- Marker pills: CALL WALL / PUT WALL / PREV CLOSE SPOT --------------
      // Drawn on `svg` rather than `chartGroup` so the label and its value can
      // sit in the right-hand axis gutter without being clipped.
      const PILL_H = 13;
      const drawMarker = (
        value: number,
        label: string,
        fill: string,
        opts: { dashed?: boolean; minor?: boolean } = {}
      ) => {
        const yRaw = gexYScale(value);
        if (yRaw < margins.top || yRaw > margins.top + chartHeight) return;

        // Pills are clamped to sit fully inside the plot area. A wall on the
        // top or bottom strike would otherwise hang half its height into the
        // axis band and collide with the tick labels. The strike box beside it
        // still states the true level, so a few pixels of nudge costs nothing.
        const y = Math.min(
          Math.max(yRaw, margins.top + PILL_H / 2 + 1),
          margins.top + chartHeight - PILL_H / 2 - 1
        );

        // Minors carry their own lighter colour (see COLORS.*Minor) and are
        // drawn at full strength — nothing here dims by tier any more. Dimming
        // read as uncertainty about the level rather than as a smaller one.

        if (opts.dashed) {
          chartGroup.append('line')
            .attr('x1', margins.left).attr('x2', margins.left + chartWidth)
            .attr('y1', yRaw).attr('y2', yRaw)
            .attr('stroke', fill)
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', '6,4');
        }

        // Tag, right-aligned to the inner edge of the chart. Three characters,
        // decoded by the legend top-left.
        const pillW = label.length * 5.1 + 12;
        const pillX = margins.left + chartWidth - pillW - 2;

        const valText = Number(value).toFixed(0);
        const valFont = w > 250 ? 10 : 9;
        const boxW = valText.length * (valFont * 0.62) + 8;
        const boxX = margins.left + chartWidth + 4;
        svg.append('rect')
          .attr('x', pillX).attr('y', y - PILL_H / 2)
          .attr('width', pillW).attr('height', PILL_H)
          .attr('rx', 2)
          .attr('fill', fill)
          // Thin dark outline: the pill sits on top of a bar in the same
          // colour, and without it the two merge into one shape.
          .attr('stroke', COLORS.background)
          .attr('stroke-width', 1);
        svg.append('text')
          .attr('x', pillX + pillW / 2).attr('y', y + 3.5)
          .attr('text-anchor', 'middle')
          .attr('fill', COLORS.pillInk)
          .attr('font-size', '8px')
          .attr('font-weight', '700')
          .attr('letter-spacing', '0.5px')
          .text(label);

        // Strike value in the axis gutter, in a boxed tag. The box is opaque so
        // it masks the regular axis tick underneath — a wall rarely lands on a
        // tick, so the two labels would otherwise collide and overlap.
        svg.append('rect')
          .attr('x', boxX).attr('y', y - PILL_H / 2)
          .attr('width', boxW).attr('height', PILL_H)
          .attr('rx', 2)
          .attr('fill', COLORS.background)
          .attr('stroke', fill)
          .attr('stroke-width', 1);
        svg.append('text')
          .attr('x', boxX + boxW / 2)
          .attr('y', y + 3.5)
          .attr('text-anchor', 'middle')
          .attr('fill', fill)
          .attr('font-size', `${valFont}px`)
          .attr('font-weight', '700')
          .text(valText);
      };

      // Marked levels, in the order the legend lists them. `tag` is what sits
      // on the chart; `name` is what the legend spells out. Three characters
      // on the bar keeps the lane narrow, and the legend carries the meaning
      // once instead of on every row.
      type MarkerSpec = {
        value: number;
        tag: string;
        name: string;
        fill: string;
        dashed?: boolean;
        minor?: boolean;
        /** Off the legend, still drawn on the chart. Default true. */
        inLegend?: boolean;
      };

      // The tag carries the tier only — blue means call, red means put, so
      // repeating the side in the text would be saying it twice.
      const markerSpecs: MarkerSpec[] = [];

      /**
       * ⚠️ Walls are a GAMMA concept, so they are drawn for the GEX metric only.
       *
       * The maths still produces a "largest positive" and "largest negative"
       * strike for Delta, Charm and Vanna — which is exactly the problem. It
       * would render a confident CALL WALL MAJOR pill and legend row on a chart
       * where no such level exists. A wall is where dealer gamma concentrates
       * and price tends to stall; the biggest vanna strike carries no such
       * meaning, and labelling it as one states something untrue.
       *
       * Prev close is deliberately outside this gate: it is the underlying's
       * price, true regardless of which greek is plotted, and it is the one
       * reference that makes the other three readable.
       *
       * The legend needs no separate condition — it is built from these specs,
       * and prev close is `inLegend: false`, so on the other metrics it has
       * nothing to list and does not render.
       */
      const showWalls = s.metric === 'gex';

      if (showWalls && callWall) {
        markerSpecs.push({ value: callWall.strike, tag: 'MAJOR', name: 'Call wall major', fill: COLORS.callGex });
      }
      if (showWalls && callWallMinor) {
        markerSpecs.push({ value: callWallMinor.strike, tag: 'MINOR', name: 'Call wall minor', fill: COLORS.callGexMinor, minor: true });
      }
      if (showWalls && putWall) {
        markerSpecs.push({ value: putWall.strike, tag: 'MAJOR', name: 'Put wall major', fill: COLORS.putGex });
      }
      if (showWalls && putWallMinor) {
        markerSpecs.push({ value: putWallMinor.strike, tag: 'MINOR', name: 'Put wall minor', fill: COLORS.putGexMinor, minor: true });
      }
      if (s.showCurrentPriceLine && currentPrice !== null) {
        markerSpecs.push({ value: currentPrice, tag: 'PREV CLOSE', name: 'Prev close', fill: COLORS.currentPrice, dashed: true, inLegend: false });
      }

      // Deferred so these draw AFTER the Y-axis below — otherwise the axis
      // tick labels paint over the gutter boxes. Minors go first so a major
      // at a nearby strike paints over them rather than under.
      const markers: Array<() => void> = [...markerSpecs]
        .sort((a, b) => Number(!!b.minor) - Number(!!a.minor))
        .map((m) => () => drawMarker(m.value, m.tag, m.fill, { dashed: m.dashed, minor: m.minor }));

      // Levels that will carry a gutter box, so the axis can drop any tick
      // that would sit behind one. Masking with an opaque box is not enough:
      // the box is clamped inside the plot area while the tick stays on its
      // true position, so near the edges the tick slides out from under it.
      const markerLevels: number[] = markerSpecs.map((m) => m.value);

      // -- Legend, top-left ---------------------------------------------------
      // Boxed panel of level names and their prices, matching the GEX-Ray
      // legend: swatch, name, value right-aligned.
      //
      // Lists every wall of the chain, including one zoomed off-screen — the
      // legend is where the levels stay fixed; only its pill is skipped (see
      // drawMarker). Sits in the top-left corner, which the bars leave free:
      // they grow from the centre line, and a left-growing bar long enough to
      // reach this corner would be the put wall, which sits low on the ladder.
      const legendSpecs = markerSpecs.filter((m) => m.inLegend !== false);
      if (legendSpecs.length && chartWidth > 230) {
        const ROW_H = 18;
        const PAD = 10;
        const COL_GAP = 26;
        const FONT = 11;

        // Archivo at 11px, semibold, tracked out — closer to 7.2px per
        // uppercase character than the 6.5 first assumed, which is why the
        // longest name ran into its value.
        const nameW = Math.max(...legendSpecs.map((m) => m.name.length)) * 7.2;
        // Number() is not decoration: `strike` is typed number but the API
        // sends it as a string, so calling .toFixed on it directly throws —
        // which is why drawMarker has always coerced it too.
        const valW =
          Math.max(...legendSpecs.map((m) => Number(m.value).toFixed(0).length)) * (FONT * 0.62);
        const boxW = PAD + nameW + COL_GAP + valW + PAD;
        const boxH = legendSpecs.length * ROW_H + PAD + 2;
        const boxX = margins.left + 8;
        const boxY = margins.top + 8;

        svg.append('rect')
          .attr('x', boxX).attr('y', boxY)
          .attr('width', boxW).attr('height', boxH)
          .attr('rx', 4)
          .attr('fill', COLORS.background)
          .attr('fill-opacity', 0.9)
          .attr('stroke', COLORS.currentPrice) // Accent Gold, as on the GEX-Ray legend
          .attr('stroke-width', 1);

        legendSpecs.forEach((m, i) => {
          const rowY = boxY + PAD + i * ROW_H + 5;

          // No swatch. The price on the right is already drawn in the level's
          // colour, so a square saying the same thing was the colour twice.
          //
          // Level names are UI chrome, so Archivo, uppercase and tracked out;
          // the price is a number, so Inter. Same split as the rest of the app.
          svg.append('text')
            .attr('x', boxX + PAD).attr('y', rowY + 4)
            .attr('fill', COLORS.textLight)
            .attr('font-family', LABEL_FONT)
            .attr('font-size', `${FONT}px`)
            .attr('font-weight', '600')
            .attr('letter-spacing', '0.6px')
            .text(m.name.toUpperCase());

          svg.append('text')
            .attr('x', boxX + boxW - PAD).attr('y', rowY + 4)
            .attr('text-anchor', 'end')
            .attr('fill', m.fill)
            .attr('font-family', NUMERIC_FONT)
            .attr('font-size', `${FONT}px`)
            .attr('font-weight', '700')
            .text(Number(m.value).toFixed(0));
        });
      }

      // Highlighted bar + crosshair when hovering
      const hoveredStrike = tooltipRef.current?.strike;
      if (hoveredStrike !== undefined) {
        const hoverY = gexYScale(hoveredStrike);
        if (hoverY >= margins.top && hoverY <= margins.top + chartHeight) {
          // Crosshair line
          chartGroup.append('line')
            .attr('x1', margins.left).attr('x2', margins.left + chartWidth)
            .attr('y1', hoverY).attr('y2', hoverY)
            .attr('stroke', COLORS.currentPrice)
            .attr('stroke-width', 0.8)
            .attr('stroke-dasharray', '3,3')
            .attr('opacity', 0.6);

          // Highlight the bar(s) at this strike
          const strikeData = validData.find((d) => d.strike === hoveredStrike);
          if (strikeData && maxValue > 0) {
            if (s.displayMode === 'separate') {
              const callW = Math.abs(Number(strikeData[callField]) || 0) * barScale;
              const putW = Math.abs(Number(strikeData[putField]) || 0) * barScale;
              if (callW > 0) {
                chartGroup.append('rect')
                  .attr('x', centerX).attr('y', hoverY - barHeight / 2)
                  .attr('width', callW).attr('height', barHeight)
                  .attr('rx', 1)
                  .attr('fill', 'none')
                  .attr('stroke', COLORS.currentPrice).attr('stroke-width', 1.5);
              }
              if (putW > 0) {
                chartGroup.append('rect')
                  .attr('x', centerX - putW).attr('y', hoverY - barHeight / 2)
                  .attr('width', putW).attr('height', barHeight)
                  .attr('rx', 1)
                  .attr('fill', 'none')
                  .attr('stroke', COLORS.currentPrice).attr('stroke-width', 1.5);
              }
            } else {
              const net = (Number(strikeData[callField]) || 0) + (Number(strikeData[putField]) || 0);
              const netW = Math.abs(net) * barScale;
              if (netW > 0) {
                chartGroup.append('rect')
                  .attr('x', net >= 0 ? centerX : centerX - netW)
                  .attr('y', hoverY - barHeight / 2)
                  .attr('width', netW).attr('height', barHeight)
                  .attr('rx', 1)
                  .attr('fill', 'none')
                  .attr('stroke', COLORS.currentPrice).attr('stroke-width', 1.5);
              }
            }
          }

          // Price label on crosshair
          chartGroup.append('rect')
            .attr('x', margins.left + chartWidth - 46)
            .attr('y', hoverY - 8)
            .attr('width', 44).attr('height', 16)
            .attr('rx', 2)
            .attr('fill', COLORS.currentPrice);
          chartGroup.append('text')
            .attr('x', margins.left + chartWidth - 24)
            .attr('y', hoverY + 4)
            .attr('text-anchor', 'middle')
            .attr('fill', '#000')
            .attr('font-size', '9px')
            .attr('font-weight', 'bold')
            .text(Number(hoveredStrike).toFixed(0));
        }
      }

      // Y-axis (outside clip, on the right)
      const yAxis = d3.axisRight(gexYScale)
        .ticks(chartHeight > 300 ? 8 : 6)
        .tickSize(0)
        .tickPadding(6);

      svg.append('g')
        .attr('transform', `translate(${margins.left + chartWidth}, 0)`)
        .call(yAxis)
        .call((g) => {
          g.select('.domain').attr('stroke', COLORS.axisLine);
          g.selectAll('.tick text')
            .attr('fill', COLORS.text)
            .attr('font-size', w > 250 ? '10px' : '9px');

          // A tick within a pill height of a marker is removed outright. Two
          // numbers fighting for the same few pixels is worse than one, and
          // the marker box states that level anyway.
          const markerYs = markerLevels.map((v) => gexYScale(v));
          g.selectAll<SVGGElement, number>('.tick')
            .filter((d) => markerYs.some((my) => Math.abs(my - gexYScale(d)) < PILL_H + 2))
            .remove();
        });

      // Y-axis separator line
      svg.append('line')
        .attr('x1', margins.left + chartWidth)
        .attr('x2', margins.left + chartWidth)
        .attr('y1', margins.top)
        .attr('y2', margins.top + chartHeight)
        .attr('stroke', COLORS.axisLine)
        .attr('stroke-width', 1);

      // Wall / spot markers last, so their boxed gutter tags sit above the
      // axis ticks instead of being overpainted by them.
      markers.forEach((draw) => draw());

      // X-axis scale labels (GEX values)
      const xLabelY = margins.top + chartHeight + 16;
      if (maxValue > 0) {
        const fmtVal = (v: number): string => {
          const a = Math.abs(v);
          if (a >= 1e9) return (v / 1e9).toFixed(1) + 'B';
          if (a >= 1e6) return (v / 1e6).toFixed(1) + 'M';
          if (a >= 1e3) return (v / 1e3).toFixed(1) + 'K';
          return v.toFixed(0);
        };

        svg.append('text').attr('x', margins.left + chartWidth * 0.05).attr('y', xLabelY)
          .attr('text-anchor', 'middle').attr('fill', COLORS.text).attr('font-size', '10px')
          .text(fmtVal(-maxValue));
        const axisTitle = (s.displayMode === 'net' ? 'NET ' : '')
          + (METRIC_LABELS[s.metric] ?? s.metric).toUpperCase();
        svg.append('text').attr('x', centerX).attr('y', xLabelY)
          .attr('text-anchor', 'middle').attr('fill', COLORS.text).attr('font-size', '10px')
          .attr('letter-spacing', '1.5px')
          .text(axisTitle);
        svg.append('text').attr('x', margins.left + chartWidth * 0.95).attr('y', xLabelY)
          .attr('text-anchor', 'middle').attr('fill', COLORS.text).attr('font-size', '10px')
          .text(fmtVal(maxValue));
      }

      // X-axis line
      svg.append('line')
        .attr('x1', margins.left).attr('x2', margins.left + chartWidth)
        .attr('y1', margins.top + chartHeight).attr('y2', margins.top + chartHeight)
        .attr('stroke', COLORS.axisLine).attr('stroke-width', 1);

      // The metric label that used to sit here was a duplicate of the axis
      // title below ('NET GEX'), and being pinned to the bottom-left corner it
      // overlapped the left-hand axis number.
    }
  }, [windowId]);

  // ================================================================
  // Setup D3 zoom (once) and attach to containers
  // ================================================================
  useEffect(() => {
    const gexContainer = gexSvgContainerRef.current;
    const candleContainer = candleCanvasRef.current;
    if (!gexContainer) return;
    // Nothing attached at all when zoom is off — cheaper than attaching a
    // behaviour whose filter always returns false, and it leaves the wheel
    // to the page.
    if (disableZoom) return;

    const zoom = d3.zoom<Element, unknown>()
      .scaleExtent([0.5, 20])
      .filter((event) => {
        // Allow wheel and drag, but not double-click (we handle that separately)
        if (event.type === 'dblclick') return false;
        return true;
      })
      .on('zoom', (event: d3.D3ZoomEvent<Element, unknown>) => {
        // Constrain to Y-axis only: keep x translation and scale at identity
        const t = event.transform;
        currentTransformRef.current = new d3.ZoomTransform(t.k, 0, t.y);
        setTooltip(null);
        renderAll();
      });

    zoomRef.current = zoom;

    // Attach zoom to GEX container
    d3.select(gexContainer as Element).call(zoom);

    // Attach same zoom to candle container if present
    if (candleContainer) {
      d3.select(candleContainer as Element).call(zoom);
    }

    // Double-click to reset zoom
    const handleDblClick = (e: MouseEvent) => {
      e.preventDefault();
      currentTransformRef.current = d3.zoomIdentity;
      d3.select(gexContainer as Element).call(zoom.transform, d3.zoomIdentity);
      if (candleContainer) {
        d3.select(candleContainer as Element).call(zoom.transform, d3.zoomIdentity);
      }
      renderAll();
    };

    gexContainer.addEventListener('dblclick', handleDblClick);
    candleContainer?.addEventListener('dblclick', handleDblClick);

    return () => {
      gexContainer.removeEventListener('dblclick', handleDblClick);
      candleContainer?.removeEventListener('dblclick', handleDblClick);
      d3.select(gexContainer as Element).on('.zoom', null);
      if (candleContainer) {
        d3.select(candleContainer as Element).on('.zoom', null);
      }
    };
  }, [renderAll, settings.showCandlesticks, disableZoom]);

  // ================================================================
  // Setup scales and render when data changes
  // ================================================================
  useEffect(() => {
    if (!gexSvgContainerRef.current) return;

    const ohlc = ohlcData;
    const gex = gexData;
    const currentPrice = ohlc && ohlc.length > 0 ? ohlc[ohlc.length - 1].close : null;
    const container = gexSvgContainerRef.current;
    const h = container.clientHeight;

    // -- Compute Y domain --
    let yMin: number;
    let yMax: number;

    if (settings.showCandlesticks && ohlc && ohlc.length > 0) {
      // Derive from candle data
      const highs = ohlc.map((b) => b.high);
      const lows = ohlc.map((b) => b.low);
      const dataMin = Math.min(...lows);
      const dataMax = Math.max(...highs);
      const padding = (dataMax - dataMin) * 0.1;
      yMin = dataMin - padding;
      yMax = dataMax + padding;
    } else if (currentPrice) {
      // Profile-only: fit to where the gamma actually is, inside a band
      // around spot. See profileRange.
      [yMin, yMax] = profileRange(gex, currentPrice);
    } else if (gex && gex.length > 0) {
      // Fallback: strike range
      const strikes = gex.map((d) => d.strike);
      yMin = Math.min(...strikes);
      yMax = Math.max(...strikes);
    } else {
      return; // No data yet
    }

    // Y scale: note inverted range (price increases upward, pixels increase downward)
    const yBase = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([h - GEX_MARGINS.bottom, GEX_MARGINS.top])
      .nice();

    yScaleBaseRef.current = yBase;

    // -- Compute X scale (for candles) --
    if (ohlc && ohlc.length > 0 && candleCanvasRef.current) {
      const candleW = candleCanvasRef.current.clientWidth;
      const dates = ohlc.map((b) => new Date(b.timestamp));
      xScaleRef.current = d3.scaleTime()
        .domain(d3.extent(dates) as [Date, Date])
        .range([MARGINS.left, candleW - MARGINS.right]);
    }

    // Reset zoom transform when data changes
    currentTransformRef.current = d3.zoomIdentity;
    if (zoomRef.current && gexSvgContainerRef.current) {
      d3.select(gexSvgContainerRef.current as Element)
        .call(zoomRef.current.transform, d3.zoomIdentity);
    }

    renderAll();
  }, [ohlcData, gexData, settings.showCandlesticks, settings.metric, settings.displayMode,
      settings.showCurrentPriceLine, renderAll]);

  // ================================================================
  // Resize observer
  // ================================================================
  useEffect(() => {
    const gexContainer = gexSvgContainerRef.current;
    const candleContainer = candleCanvasRef.current;
    if (!gexContainer) return;

    const observer = new ResizeObserver(() => {
      // Recalibrate scale ranges on resize
      const yBase = yScaleBaseRef.current;
      if (yBase) {
        const h = gexContainer.clientHeight;
        yBase.range([h - GEX_MARGINS.bottom, GEX_MARGINS.top]);
      }
      if (xScaleRef.current && candleContainer) {
        const candleW = candleContainer.clientWidth;
        xScaleRef.current.range([MARGINS.left, candleW - MARGINS.right]);
      }
      renderAll();
    });

    observer.observe(gexContainer);
    if (candleContainer) observer.observe(candleContainer);

    return () => observer.disconnect();
  }, [renderAll, settings.showCandlesticks]);

  // ================================================================
  // Tooltip on hover (GEX panel)
  // ================================================================
  const handleGexMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const gex = gexDataRef.current;
    const yBase = yScaleBaseRef.current;
    if (!gex || gex.length === 0 || !yBase || !gexSvgContainerRef.current) {
      setTooltip(null);
      return;
    }

    const rect = gexSvgContainerRef.current.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const margins = rect.width > 250 ? GEX_MARGINS : GEX_MARGINS_NARROW;
    const chartHeight = rect.height - margins.top - margins.bottom;

    if (mouseY < margins.top || mouseY > margins.top + chartHeight) {
      setTooltip(null);
      return;
    }

    // Use transformed scale (high prices = small Y = top)
    const yScale = currentTransformRef.current.rescaleY(yBase)
      .copy()
      .range([rect.height - margins.bottom, margins.top]);
    const mousePrice = yScale.invert(mouseY);

    const s = settingsRef.current;
    const callField = `call_${s.metric}` as keyof GexStrikeData;
    const putField = `put_${s.metric}` as keyof GexStrikeData;

    // Find nearest strike
    let nearest: GexStrikeData | null = null;
    let minDist = Infinity;
    for (const d of gex) {
      const dist = Math.abs(d.strike - mousePrice);
      if (dist < minDist) {
        minDist = dist;
        nearest = d;
      }
    }

    if (nearest) {
      const pxPerDollar = chartHeight / (yScale.invert(margins.top) - yScale.invert(margins.top + chartHeight));
      const distPx = minDist * Math.abs(pxPerDollar);
      if (distPx <= 15) {
        const callVal = Number(nearest[callField]) || 0;
        const putVal = Number(nearest[putField]) || 0;
        setTooltip({
          // Clamped here, in the handler, rather than at render time.
          // `rect` is measured on this event, so it is the CURRENT width;
          // reading a ref during render gives whatever was measured on the
          // previous pass, which puts the first tooltip after a resize in the
          // wrong place. Refs are not reactive, so React cannot re-render to
          // correct it.
          x: Math.min(e.clientX - rect.left + 12, rect.width - 130),
          y: mouseY,
          strike: nearest.strike,
          callValue: callVal,
          putValue: putVal,
          netValue: callVal + putVal,
        });
        return;
      }
    }
    setTooltip(null);
  }, []);

  const handleGexMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  // Re-render when tooltip changes (for crosshair + bar highlight)
  useEffect(() => {
    renderAll();
  }, [tooltip, renderAll]);

  // ================================================================
  // Tooltip on hover (Candle panel)
  // ================================================================
  const handleCandleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const ohlc = ohlcDataRef.current;
    const xScale = xScaleRef.current;
    if (!ohlc || ohlc.length === 0 || !xScale || !candleCanvasRef.current) {
      setCandleTooltip(null);
      return;
    }

    const rect = candleCanvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    if (mouseX < MARGINS.left || mouseX > rect.width - MARGINS.right ||
        mouseY < MARGINS.top || mouseY > rect.height - MARGINS.bottom) {
      setCandleTooltip(null);
      return;
    }

    // Find nearest candle by X position
    const mouseDate = xScale.invert(mouseX);
    let nearest = ohlc[0];
    let minDist = Infinity;
    for (const bar of ohlc) {
      const barDate = new Date(bar.timestamp);
      const dist = Math.abs(barDate.getTime() - mouseDate.getTime());
      if (dist < minDist) {
        minDist = dist;
        nearest = bar;
      }
    }

    const barX = xScale(new Date(nearest.timestamp));
    const distPx = Math.abs(barX - mouseX);
    if (distPx <= 30) {
      const d = new Date(nearest.timestamp);
      setCandleTooltip({
        x: e.clientX - rect.left,
        y: mouseY,
        date: `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`,
        open: nearest.open,
        high: nearest.high,
        low: nearest.low,
        close: nearest.close,
      });
    } else {
      setCandleTooltip(null);
    }
  }, []);

  const handleCandleMouseLeave = useCallback(() => {
    setCandleTooltip(null);
  }, []);

  // ================================================================
  // Pane divider drag handlers
  // ================================================================
  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dividerDragRef.current = { startX: e.clientX, startWidth: gexPanelWidth };
    let latestWidth = gexPanelWidth;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dividerDragRef.current) return;
      // Dragging left = larger GEX panel (deltaX is negative)
      const delta = dividerDragRef.current.startX - ev.clientX;
      latestWidth = Math.max(120, Math.min(600, dividerDragRef.current.startWidth + delta));
      setGexPanelWidth(latestWidth);
    };

    const handleMouseUp = () => {
      dividerDragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist to settings for backend save
      setSettings((prev) => ({ ...prev, gexPanelWidth: latestWidth }));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [gexPanelWidth]);

  // ================================================================
  // JSX
  // ================================================================
  // ⚠️ A disabled query stays `isPending` forever, so `awaitingExpiry` must be
  // excluded here or the pane spins indefinitely instead of explaining itself.
  const isLoading = !awaitingExpiry && (ohlcLoading || gexLoading);
  const hasError = ohlcError || gexError;

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
          {title ?? (
            <>Gamma Exposure {activeSymbol && <>— <span className="text-accent-yellow">{activeSymbol}</span></>}</>
          )}
          {/* The resolved expiry is only known once the contract list loads,
              so a caller-supplied title cannot include it — it is appended
              here instead. */}
          {title && settings.byExpiration && settings.expiryDate && (
            <span className="ml-2 text-accent-yellow font-numeric">
              {fmtExpiryLabel(settings.expiryDate)}
            </span>
          )}
        </h3>
      </div>

      {/* Toolbar. Hidden entirely where the host configures the widget and
          presents it as a fixed readout. */}
      {!hideControls && (
      <div className="px-3 py-1.5 border-b border-dark-border flex items-center gap-2 bg-dark-bg/50 flex-wrap relative z-20">
        {!lockedSymbol && (
        <form onSubmit={handleSymbolSubmit} className="flex items-center gap-1">
          <input
            type="text"
            value={symbolInput}
            onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
            placeholder="Symbol"
            readOnly={isTickerLinked}
            title={isTickerLinked ? 'Linked — set the ticker from another linked widget or unlink to edit' : undefined}
/**
                 ⚠️ Read-only while linked. The symbol is owned by the link
                 group, so editing it here would either be silently discarded
                 or would move every other linked pane — neither of which a
                 plain-looking text field leads anyone to expect. The gold
                 border is the tell. */
            className={`w-16 px-2 py-1 text-xs border rounded text-accent-yellow font-semibold tracking-wide placeholder:font-normal placeholder:tracking-normal placeholder-gray-500 focus:outline-none ${
              isTickerLinked
                ? 'bg-dark-surface border-accent-yellow ring-1 ring-accent-yellow/40 cursor-default select-none'
                : 'bg-dark-surface border-dark-border focus:border-accent-yellow'
            }`}
          />
          <button
            type="submit"
            className="px-2 py-1 text-xs font-medium bg-accent-yellow hover:bg-wheat text-hot-tag-text rounded transition-colors"
          >
            Go
          </button>
        </form>
        )}
        {!lockedSymbol && (
          <TickerLinkButton
            windowId={windowId}
            currentTicker={settings.symbol}
            onTickerChange={handleSymbolSelect}
          />
        )}

        <select
          value={settings.metric}
          onChange={(e) => setSettings((prev) => ({ ...prev, metric: e.target.value as GexMetric }))}
          className="text-xs bg-dark-surface border border-dark-border rounded px-2 py-1 text-gray-300 focus:outline-none focus:border-accent-yellow"
        >
          {Object.entries(METRIC_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={settings.displayMode}
          onChange={(e) => setSettings((prev) => ({ ...prev, displayMode: e.target.value as GexDisplayMode }))}
          className="text-xs bg-dark-surface border border-dark-border rounded px-2 py-1 text-gray-300 focus:outline-none focus:border-accent-yellow"
        >
          <option value="separate">Calls + Puts</option>
          <option value="net">Net</option>
        </select>

        {/* Legend sits at the far right of the toolbar, round dots per mockup */}
        <span className="ml-auto flex items-center gap-3 text-[10px] text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.callGex }}></span>
            Calls
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.putGex }}></span>
            Puts
          </span>
        </span>

        {/* Separates the selectors from the expiration control */}
        <div className="w-px h-4 bg-dark-border flex-shrink-0 mx-1" />

        {/* Expiration filter.
            One control, not a mode switch plus a picker. "All" is simply the
            first option, so any state — aggregate or a specific contract — is
            one click away and the closed control always states which is
            active. A segmented pair would be right for two states, as in Net
            Sweeper Flow; here there are as many states as the symbol has
            expiries, and the second control would be redundant with this. */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[1px] text-gray-400">Exp</span>

          {expirations.length ? (
            <select
              value={settings.byExpiration ? settings.expiryDate || '' : ALL_EXPIRIES}
              onChange={(e) => {
                const next = e.target.value;
                setSettings((prev) => ({
                  ...prev,
                  byExpiration: next !== ALL_EXPIRIES,
                  expiryDate: next === ALL_EXPIRIES ? null : next,
                }));
              }}
              className="text-xs bg-dark-surface border border-dark-border rounded px-2 py-1 text-gray-300 focus:outline-none focus:border-accent-yellow"
            >
              <option value={ALL_EXPIRIES}>All expiries</option>
              {/* ⚠️ A date can appear TWICE — index monthlies carry an
                  AM-settled and a PM-settled chain on the same third Friday.
                  Keyed on (expiry, root) because the date alone is not unique.

                  The settlement suffix only shows where it disambiguates, so
                  ordinary weeklies stay clean.

                  ⚠️ `value` is still the bare date, so picking either row of a
                  monthly pair selects the same expiry. Settlement-aware
                  selection means widening the persisted `expiryDate`, which
                  every saved widget's settings would have to migrate — out of
                  scope here. The Gamma tab does not show this control. */}
              {expirations.map((exp, i) => {
                const dte = daysToExpiry(exp.expiry);
                const shared = expirations.some(
                  (o, j) => j !== i && o.expiry === exp.expiry
                );
                return (
                  <option key={`${exp.expiry}:${exp.root}`} value={exp.expiry}>
                    {fmtExpiryLabel(exp.expiry)}
                    {shared ? ` (${exp.settlement})` : ''} &middot;{' '}
                    {dte === 0 ? '0DTE' : `${dte}d`}
                  </option>
                );
              })}
            </select>
          ) : (
            /*
              No expiry list yet — the same control, disabled, reading
              "All expiries", which is exactly what the chart is showing.

              ⚠️ It replaced a Next/All toggle plus a free date box. Two
              reasons that had to go: it was a second, differently-shaped
              control for the same setting, so the widget changed appearance
              depending on whether a fetch had succeeded; and the date box
              accepted any date at all, including ones with no listed
              contract, which renders an empty chart that looks like a bug
              rather than a bad input.

              Nothing else is needed here — the dropdown above maps straight
              off `expirations`, so it populates on its own once the list is
              there and this branch stops rendering.
            */
            <select
              disabled
              value={ALL_EXPIRIES}
              className="text-xs bg-dark-surface border border-dark-border rounded px-2 py-1 text-gray-500 opacity-60 cursor-not-allowed focus:outline-none"
              title="Expiry list unavailable — showing every expiry aggregated"
            >
              <option value={ALL_EXPIRIES}>All expiries</option>
            </select>
          )}
        </div>

        <button
          onClick={handleRefresh}
          className="px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
          title="Refresh"
        >
          ↻
        </button>

        <ViewButton
          label="Chart"
          isActive={settings.showCandlesticks}
          onClick={() => setSettings((prev) => ({ ...prev, showCandlesticks: !prev.showCandlesticks }))}
        />

        <div className="flex-1" />

        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`text-gray-400 hover:text-white text-sm px-2 py-0.5 rounded transition-colors ${
            showSettings ? 'bg-dark-border text-white' : ''
          }`}
          title="Settings"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="px-3 py-2 border-b border-dark-border bg-dark-bg/50 relative z-20">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Days Back:</span>
              <input
                type="number"
                value={settings.daysBack}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0 && val <= 365) {
                    setSettings((prev) => ({ ...prev, daysBack: val }));
                  }
                }}
                min="1"
                max="365"
                className="w-16 px-2 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white focus:outline-none focus:border-accent-yellow"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showCurrentPriceLine}
                onChange={(e) => setSettings((prev) => ({ ...prev, showCurrentPriceLine: e.target.checked }))}
                className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow"
              />
              <span className="text-gray-300">Current Price Line</span>
            </label>
          </div>
        </div>
      )}

      {/* Chart Container */}
      <div className="relative flex-1 flex overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-surface/80 z-10">
            <span className="text-gray-400 text-sm">Loading...</span>
          </div>
        )}

        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-surface/80 z-10">
            <span className="text-bear-red text-sm">Error loading data</span>
          </div>
        )}

        {/* Pinned to a single expiry that could not be resolved. Not an error
            in red: the symbol may simply have no listed contracts, and the
            panel beside it is fine. It just must not draw a chart, because any
            chart it drew here would be of different data than its title
            claims. */}
        {awaitingExpiry && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-surface/80 z-10 px-4">
            <span className="text-gray-400 text-sm text-center">
              No expiry data available{activeSymbol ? ` for ${activeSymbol}` : ''}
            </span>
          </div>
        )}

        {/* Candlestick Canvas Panel */}
        {settings.showCandlesticks && (
          <div
            ref={candleCanvasRef}
            className="flex-1 min-w-0 h-full relative"
            style={{ cursor: 'grab', background: COLORS.background }}
            onMouseMove={handleCandleMouseMove}
            onMouseLeave={handleCandleMouseLeave}
          >
            <canvas
              ref={canvasRef}
              className="absolute inset-0"
            />

            {/* Candle hover tooltip */}
            {candleTooltip && (
              <div
                className="absolute pointer-events-none z-10 px-2 py-1.5 rounded text-xs border whitespace-nowrap"
                style={{
                  left: Math.min(candleTooltip.x + 12, (candleCanvasRef.current?.clientWidth ?? 400) - 130),
                  top: Math.max(4, candleTooltip.y - 50),
                  backgroundColor: '#161616ee',
                  borderColor: '#2b2b2b',
                  color: '#c3c1bb',
                }}
              >
                <div className="font-medium text-white">{candleTooltip.date}</div>
                <div>O: {Number(candleTooltip.open).toFixed(2)}</div>
                <div>H: {Number(candleTooltip.high).toFixed(2)}</div>
                <div>L: {Number(candleTooltip.low).toFixed(2)}</div>
                <div style={{ color: Number(candleTooltip.close) >= Number(candleTooltip.open) ? COLORS.candleUp : COLORS.candleDown }}>
                  C: {Number(candleTooltip.close).toFixed(2)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pane Divider */}
        {settings.showCandlesticks && (
          <div
            className="w-1 h-full shrink-0 cursor-col-resize hover:bg-bull-blue/30 active:bg-bull-blue/50 transition-colors"
            style={{ background: COLORS.axisLine }}
            onMouseDown={handleDividerMouseDown}
          />
        )}

        {/* GEX Profile SVG Panel */}
        <div
          ref={gexSvgContainerRef}
          className={`${settings.showCandlesticks ? 'shrink-0' : 'flex-1'} h-full relative`}
          style={{
            cursor: 'grab',
            background: COLORS.background,
            width: settings.showCandlesticks ? `${gexPanelWidth}px` : undefined,
            minWidth: settings.showCandlesticks ? '120px' : undefined,
          }}
          onMouseMove={handleGexMouseMove}
          onMouseLeave={handleGexMouseLeave}
        >
          <svg
            ref={svgRef}
            className="absolute inset-0"
            style={{ overflow: 'hidden' }}
          />

          {/* Hover tooltip */}
          {tooltip && (
            <div
              className="absolute pointer-events-none z-10 px-2 py-1.5 rounded text-xs border whitespace-nowrap"
              style={{
                // Already clamped to the container when the tooltip was set.
                left: tooltip.x,
                top: Math.max(4, tooltip.y - 36),
                backgroundColor: '#161616ee',
                borderColor: '#2b2b2b',
                color: '#c3c1bb',
              }}
            >
              <div className="font-medium text-white">${tooltip.strike}</div>
              {settings.displayMode === 'net' ? (
                <div>Net: {formatGexValue(tooltip.netValue)}</div>
              ) : (
                <>
                  <div style={{ color: COLORS.callGex }}>Calls: {formatGexValue(tooltip.callValue)}</div>
                  <div style={{ color: COLORS.putGex }}>Puts: {formatGexValue(tooltip.putValue)}</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
